import {
  AmplitudeProductEvent,
  hasLikelySmartSearchIntent,
} from '@cfa/pathway-fe-shared';

import type { Track } from '@/amplitude/useAmplitude';

/** Per-query one-shot flag set on rotating-prompt submit (PATHWAY-3666). */
const searchSubmittedViaPromptSuggestionByQuery = new Map<string, boolean>();

const promptSuggestionQueryKey = (query: string): string =>
  query.trim() || 'empty';

/** Dedupes search_result when the same page-1 response is handled twice. */
let lastTrackedSearchResultQueryId: string | null = null;

export const setSearchSubmittedViaPromptSuggestion = (
  query: string,
  viaPrompt: boolean,
): void => {
  const key = promptSuggestionQueryKey(query);
  if (viaPrompt) {
    searchSubmittedViaPromptSuggestionByQuery.set(key, true);
  } else {
    searchSubmittedViaPromptSuggestionByQuery.delete(key);
  }
};

export const consumeSearchSubmittedViaPromptSuggestion = (
  query: string,
): boolean => {
  const key = promptSuggestionQueryKey(query);
  const value = searchSubmittedViaPromptSuggestionByQuery.get(key) ?? false;
  searchSubmittedViaPromptSuggestionByQuery.delete(key);
  return value;
};

/** Reset module state — tests only. */
export const resetExploreSearchAnalyticsState = (): void => {
  searchSubmittedViaPromptSuggestionByQuery.clear();
  lastTrackedSearchResultQueryId = null;
};

/**
 * Fire `search_result` after an effective explore GET /search page-1 response.
 * Call only from `useExploreSearch` queryFn (not on cache hits or pagination).
 */
export const trackExploreSearchResult = ({
  track,
  query,
  queryId,
  numResults,
  smartSearchEnabled,
}: {
  track: Track | null;
  query: string;
  queryId: string | undefined;
  numResults: number;
  smartSearchEnabled: boolean;
}): void => {
  if (!track) {
    return;
  }
  if (queryId && lastTrackedSearchResultQueryId === queryId) {
    return;
  }
  lastTrackedSearchResultQueryId = queryId ?? null;

  track(AmplitudeProductEvent.SEARCH_RESULT, {
    num_results: numResults,
    query: query || 'empty',
    smart_search_enabled: smartSearchEnabled,
    smart_search_intent: hasLikelySmartSearchIntent(query),
    submitted_via_prompt_suggestion:
      consumeSearchSubmittedViaPromptSuggestion(query),
  });
};
